import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import type { TaskStatisticsResultType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import { useAuth } from 'context/AuthContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { canManageTaskDir } from 'utils/helpers/access/hasUserAccess';

const useTaskDashboard = () => {
    const { auth } = useAuth();
    const params = useSettingsParams();
    const userId = auth?.user?.ID;
    const role = auth?.user?.access?.role;
    const enabled = Boolean(userId && params.tasksModule && ['manager', 'seller', 'executor'].includes(role));
    const canViewProcesses = canManageTaskDir(auth?.user);
    const [data, setData] = useState<TaskStatisticsResultType | null>(null);
    const [loading, setLoading] = useState(true);
    const [revision, setRevision] = useState(0);
    const viewAll = Boolean(auth?.user?.admin || auth?.user?.access?.tasksViewAll);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        setData(null);
        setLoading(true);
        const today = DateTime.local();
        const load = async () => {
            try {
                const result = await apiTask.getStatistics({
                    dayStart: today.startOf('day').toMillis(),
                    dayEnd: today.endOf('day').toMillis(),
                });
                if (!cancelled) setData(result);
            } catch {
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [enabled, userId, role, viewAll, revision]);

    return { enabled, canViewProcesses, data, loading, retry: () => setRevision((value) => value + 1) };
};

export default useTaskDashboard;
